"use client";

import Image from "next/image";
import Link from "next/link";
import { Icon } from "@iconify/react";
import { Button } from "../ui/button";
import dynamic from "next/dynamic";
import { Suspense, useEffect, useRef, useState } from "react";
import { useTranslation, Trans } from 'react-i18next';

// Lazy load the 3D scene to prevent blocking the main thread and reduce initial JS payload
const AboutScene = dynamic(() => import("../3d/AboutScene").then((mod) => mod.AboutScene), {
  ssr: false,
  loading: () => <div className="h-full flex items-center justify-center">Loading 3D Model...</div>
});

export function Header() {
  const { t } = useTranslation();
  const [show3D, setShow3D] = useState(false);

  // Defer 3D rendering until interaction or timeout to save main thread blocking during FCP/LCP
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const handleInteraction = () => {
      setShow3D(true);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      clearTimeout(timeout);
    };

    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('scroll', handleInteraction);

    timeout = setTimeout(() => {
      handleInteraction();
    }, 3500);

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      clearTimeout(timeout);
    };
  }, []);

  // Hero entrance is CSS-only (see globals.css .hero-fade-up): a GSAP timeline
  // here left the hero stuck at opacity 0 when React discarded the hydrated
  // tree after the locale hydration mismatch, because the tweened nodes were
  // replaced mid-animation. CSS animations survive tree regeneration.
  const headerRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <header
        ref={headerRef}
        className="flex flex-col space-y-4 mb-10 pt-0"
      >
        {/* Open-canvas minimalist hero */}
        <div ref={cardRef} className="relative pt-4 sm:pt-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div ref={textRef} className="text-left">
              {/* Small portrait + availability */}
              <div className="hero-fade-up mb-6 flex items-center gap-4">
                <div ref={imgRef} className="relative size-14 sm:size-16 shrink-0">
                  <div className="h-full w-full overflow-hidden rounded-full border border-slate-200 dark:border-slate-700 shadow-sm bg-slate-100 dark:bg-slate-800">
                    <Image
                      src="/images/ellis.jpg"
                      alt="Ellis Velandia Caicedo"
                      width={128}
                      height={128}
                      className="object-cover object-[50%_22%]"
                      priority
                      quality={80}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    {t('Header.availability')}
                  </span>
                </div>
              </div>

              <h1 className="mb-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#181A1D] dark:text-foreground">
                {(() => {
                  const headline = (t('Header.headline') as string).replace(/\.\s*$/, '');
                  const words = headline.split(' ');
                  return words.map((word, i) => (
                    <span key={`${word}-${i}`} className="word-mask">
                      <span
                        className={`word-rise ${i === words.length - 1 ? 'text-primary' : ''}`}
                        style={{ animationDelay: `${0.15 + i * 0.08}s` }}
                      >
                        {word}
                      </span>
                      {i < words.length - 1 ? ' ' : ''}
                    </span>
                  ));
                })()}
              </h1>
              <p
                className="hero-fade-up mb-8 max-w-xl text-base sm:text-lg text-slate-700 dark:text-muted-foreground"
                style={{ animationDelay: '0.55s' }}
              >
                {t('Header.subheadline')}
              </p>

              <div className="hero-fade-up mb-10 flex flex-wrap items-center gap-5" style={{ animationDelay: '0.7s' }}>
                <Button asChild className="rounded-full px-7 font-bold">
                  <Link href="/contact">{t('Header.getInTouch')}</Link>
                </Button>
                <Link
                  href="/projects"
                  className="text-sm font-semibold underline underline-offset-8 decoration-slate-300 dark:decoration-slate-700 hover:decoration-current transition-colors"
                >
                  {t('Header.viewWork')}
                </Link>
              </div>

              <div
                ref={bioRef}
                className="hero-fade-up max-w-xl border-t border-slate-200 pt-6 text-sm leading-relaxed text-foreground dark:border-slate-800 sm:text-base"
                style={{ animationDelay: '0.85s', opacity: 0.7 }}
              >
                <p className="whitespace-pre-line">{t('About.hero.description')}</p>
              </div>
            </div>

            {/* Floating 3D Laptop */}
            <div ref={sceneRef} data-parallax="0.12" className="relative hidden h-[260px] w-full lg:block">
              {show3D ? (
                <Suspense
                  fallback={
                    <div className="h-full flex items-center justify-center">
                      Loading 3D Model...
                    </div>
                  }
                >
                  <AboutScene />
                </Suspense>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <Icon icon="carbon:laptop" className="h-12 w-12 text-slate-300 dark:text-slate-700 animate-pulse" />
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
